import type { Dayjs } from 'dayjs';
import type { Application } from '../../../types';
import type { OriginalsDispatchMethod, RegisterOriginalsDispatchBody } from '../types';
import { trimOptionalComment } from './commentPolicy';
import {
  validateTaskModalEventDate,
  validateTaskModalRequiredText,
} from './taskModalValidation';

export type SendOriginalsValidationCode =
  | 'EVENT_DATE_REQUIRED'
  | 'DISPATCH_METHOD_REQUIRED'
  | 'TRACKING_REQUIRED'
  | 'ESTIMATED_RECEIPT_REQUIRED';

export type SendOriginalsValidationIssue = {
  code: SendOriginalsValidationCode;
  message: string;
};

export type ValidateSendOriginalsFormInput = {
  dispatchedAt: Dayjs | null | undefined;
  dispatchMethod: OriginalsDispatchMethod | null | undefined;
  trackingNumber: string | null | undefined;
  estimatedReceiptAt: Dayjs | null | undefined;
  comment: string | null | undefined;
};

export const ORIGINALS_DISPATCH_METHOD_OPTIONS: Array<{
  value: OriginalsDispatchMethod;
  label: string;
}> = [
  { value: 'MAIL', label: 'Почта' },
  { value: 'COURIER', label: 'Курьер' },
  { value: 'CARRIER', label: 'Транспортная компания' },
  { value: 'HAND', label: 'Лично' },
  { value: 'OTHER', label: 'Другое' },
];

export function buildDefaultOriginalsDispatchRecipient(
  application: Application | null | undefined
): string {
  const clientName = application?.client?.name?.trim();
  const consigneeName = application?.consignee?.name?.trim();
  return clientName || consigneeName || 'Клиент';
}

export function validateSendOriginalsSubmit(
  input: ValidateSendOriginalsFormInput
): SendOriginalsValidationIssue | null {
  const dateIssue =
    (validateTaskModalEventDate(
      input.dispatchedAt,
      'Укажите дату отправки'
    ) as SendOriginalsValidationIssue | null) ??
    (validateTaskModalEventDate(
      input.estimatedReceiptAt,
      'Укажите ориентировочную дату получения'
    ) as SendOriginalsValidationIssue | null);

  if (dateIssue) {
    return dateIssue;
  }

  if (!input.dispatchMethod) {
    return {
      code: 'DISPATCH_METHOD_REQUIRED',
      message: 'Укажите способ доставки',
    };
  }

  const trackingIssue = validateTaskModalRequiredText(
    input.trackingNumber,
    'TRACKING_REQUIRED',
    'Укажите трек-номер'
  ) as SendOriginalsValidationIssue | null;

  if (trackingIssue) {
    return trackingIssue;
  }

  return null;
}

export function buildOriginalsDispatchBody(input: {
  recipient: string;
  dispatchMethod: OriginalsDispatchMethod;
  trackingNumber: string;
  comment: string | null | undefined;
  dispatchedAt: string;
  estimatedReceiptAt: string;
}): RegisterOriginalsDispatchBody {
  const body: RegisterOriginalsDispatchBody = {
    dispatchMethod: input.dispatchMethod,
    recipient: input.recipient.trim(),
    trackingNumber: input.trackingNumber.trim(),
    dispatchedAt: input.dispatchedAt,
    estimatedReceiptAt: input.estimatedReceiptAt,
  };
  const trimmedComment = trimOptionalComment(input.comment);
  if (trimmedComment) {
    body.comment = trimmedComment;
  }
  return body;
}
